/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import { Icon, Button, Tooltip, Stack, Box } from 'sarsaparilla';
import { GridCell } from 'ui-permit/src/components/detailed-availability-page-new/Grid';

const propTypes = {
    dateLabel: PropTypes.string,
    count: PropTypes.number,
    total: PropTypes.number,
    reserved: PropTypes.number,
    dailyCount: PropTypes.number,
    dailyTotal: PropTypes.number,
    weeklyCount: PropTypes.number,
    weeklyTotal: PropTypes.number,
    isSelected: PropTypes.bool,
    isSelectable: PropTypes.bool,
    isChecked: PropTypes.bool,
    isWalkup: PropTypes.bool,
    divisionName: PropTypes.string,
    onSelectToggle: PropTypes.func,
};

const defaultProps = {
    dailyCount: null,
    dailyTotal: null,
    weeklyCount: null,
    weeklyTotal: null,
    isSelected: false,
    isSelectable: true,
};

export default class AvailabilityCell extends React.Component {
    renderUnavailable = (displayCount, label) => {
        return (
            <GridCell className="unavailable">
                <div className="rec-full-button-wrap">
                    <Tooltip content={label}>
                        <div className="rec-availability-date" aria-label={label}>
                            {displayCount}
                        </div>
                    </Tooltip>
                </div>
            </GridCell>
        );
    };

    renderTooltip = (lines = []) => (
        <Stack space="xxs">
            {lines.map((line, index) => (
                <Box key={index} textAlign="center">
                    {line}
                </Box>
            ))}
        </Stack>
    );

    render() {
        let countLabel = this.props.count;
        if (this.props.total >= 9999) {
            countLabel = '99+';
        }
        if (this.props.isWalkup) {
            countLabel = 'W';
        }

        let check = null;
        if (this.props.isChecked) {
            check = <Icon iconName="check" />;
        } else {
            check = <span>&nbps;</span>;
        }

        let className = 'available';
        if (!this.props.isSelectable) {
            className = 'unavailable';
            countLabel = '0';
        }
        if (this.props.isSelected) {
            className = 'selected';
        }
        if (this.props.isWalkup) {
            className = 'walk-up';
        }

        let dailyCountLabel = '';
        if (
            this.props.isSelectable &&
            this.props.dailyCount !== null &&
            this.props.dailyCount > 0 &&
            this.props.dailyTotal > 0
        ) {
            dailyCountLabel = `\u000AOvernight Quota: ${this.props.dailyCount} of ${this.props.dailyTotal}`;
        }

        let weeklyCountLabel = '';
        if (
            this.props.isSelectable &&
            this.props.weeklyCount !== null &&
            this.props.weeklyCount > 0 &&
            this.props.weeklyTotal > 0
        ) {
            weeklyCountLabel = `\u000A Weekly Quota: ${this.props.weeklyCount} of ${this.props.weeklyTotal}`;
        }

        const dateStr = this.props.dateLabel ? `${this.props.dateLabel} \u000A ` : '';
        let label = this.renderTooltip([
            dateStr,
            `Available: ${countLabel}`,
            dailyCountLabel,
            weeklyCountLabel,
        ]);

        if (this.props.isSelected) {
            return (
                <GridCell className={className}>
                    <div className="rec-full-button-wrap">
                        <Tooltip content={label}>
                            <Button
                                className="rec-availability-date"
                                isDisabled={!this.props.isSelectable}
                                onClick={this.props.onSelectToggle}
                                aria-label={label}
                                screenReaderTextBefore={`deselect ${this.props.divisionName} on ${dateStr}`}
                                iconBeforeElement={check}
                            />
                        </Tooltip>
                    </div>
                </GridCell>
            );
        }
        if (this.props.count && this.props.count > 0) {
            if (this.props.isSelectable) {
                label = this.renderTooltip([
                    dateStr,
                    `Available: ${countLabel}`,
                    dailyCountLabel,
                    weeklyCountLabel,
                ]);

                return (
                    <GridCell className={className}>
                        <div className="rec-full-button-wrap">
                            <Tooltip content={label}>
                                <Button
                                    className="rec-availability-date"
                                    onClick={this.props.onSelectToggle}
                                    screenReaderTextBefore={`select ${this.props.divisionName} on ${dateStr}`}
                                    aria-label={label}
                                >
                                    {countLabel}
                                </Button>
                            </Tooltip>
                        </div>
                    </GridCell>
                );
            }
            label = this.renderTooltip([
                dateStr,
                `Available: ${countLabel}`,
                dailyCountLabel,
                weeklyCountLabel,
            ]);
            return this.renderUnavailable(countLabel, label);
        }
        if (this.props.count === 0 && !this.props.isWalkup) {
            label = this.renderTooltip([
                dateStr,
                'Unavailable',
                `Available: ${countLabel}`,
                dailyCountLabel,
                weeklyCountLabel,
            ]);
            return this.renderUnavailable(countLabel, label);
        }
        if (this.props.isWalkup) {
            label = this.renderTooltip([
                dateStr,
                'Walk-up',
                `Available: ${countLabel}`,
                dailyCountLabel,
                weeklyCountLabel,
            ]);
            return (
                <GridCell className="walk-up">
                    <div className="rec-full-button-wrap">
                        <Tooltip content={label}>
                            <div className="rec-availability-date" aria-label={label}>
                                W
                            </div>
                        </Tooltip>
                    </div>
                </GridCell>
            );
        }

        label = this.renderTooltip([
            dateStr,
            'Unavailable',
            'There is no availability on this date',
        ]);
        return this.renderUnavailable(0, label);
    }
}

AvailabilityCell.propTypes = propTypes;
AvailabilityCell.defaultProps = defaultProps;
